import React, { useState, useCallback, useMemo } from 'react';
import { FaFileExcel, FaSpinner } from 'react-icons/fa';
import { FaCheckCircle } from 'react-icons/fa';
import { FaCreditCard } from 'react-icons/fa';
import { FaBan } from 'react-icons/fa';
import { FaUndo } from 'react-icons/fa';
import { useAccess } from '../../../context/AccessContext';

/**
 * 캠페인 액션 바 컴포넌트
 * @param {Object} props
 * @param {Array} props.actions - 액션 정의 배열 (action.requires 권한 키가 있으면 권한이 있을 때만 렌더 — R-FE-04)
 * @param {Array} props.selectedIds - 선택된 ID 배열
 * @param {Array} props.items - 전체 아이템 배열 (enabledWhen에서 사용)
 * @param {Function} props.onAction - 액션 실행 핸들러
 * @param {Function} [props.onRefundOpen] - 환불 모달 열기 핸들러 (selectedIds, items) => void
 * @param {boolean} props.isLoading - 로딩 상태
 */
function CampaignActionBar({ actions = [], selectedIds = [], items = [], onAction, onRefundOpen, isLoading = false }) {
  const [actionLoading, setActionLoading] = useState({});
  const { hasPermission } = useAccess();

  const handleAction = useCallback(async (action) => {
    // enabledWhen 확인
    if (action.enabledWhen && !action.enabledWhen(selectedIds, items)) {
      return;
    }

    // refund: onRefundOpen 핸들러가 있으면 모달 열기
    if (action.actionType === 'refund' && onRefundOpen) {
      if (selectedIds.length > 10) {
        alert('환불 처리는 한 번에 10건 이하만 선택할 수 있습니다.');
        return;
      }
      onRefundOpen(selectedIds, items);
      return;
    }

    // confirm 확인
    if (action.confirm) {
      const message = typeof action.confirm === 'string' 
        ? action.confirm 
        : action.confirm(selectedIds, items);
      if (!window.confirm(message)) {
        return;
      }
    }

    // payloadBuilder로 payload 생성
    let payload = {};
    if (action.payloadBuilder) {
      payload = action.payloadBuilder(selectedIds, items);
    } else {
      // 기본 payload (일반적인 경우)
      const idField = action.idField || 'ids';
      payload[idField] = selectedIds;
    }

    // 로딩 상태 설정
    setActionLoading(prev => ({ ...prev, [action.id]: true }));

    try {
      await onAction(action.actionType, payload, action);
    } finally {
      setActionLoading(prev => ({ ...prev, [action.id]: false }));
    }
  }, [onAction, onRefundOpen, selectedIds, items]);

  const getActionIcon = useCallback((actionType, actionIcon) => {
    switch (actionType) {
      case 'download':
        return <FaFileExcel />;
      case 'submitted':
      case 'complete':
        return <FaCheckCircle />;
      case 'paid':
        return <FaCreditCard />;
      case 'refund':
        return <FaUndo />;
      case 'delete':
        return <FaBan />;
      default:
        return actionIcon || null;
    }
  }, []);

  const getActionClassName = useCallback((actionType) => {
    const baseClass = actionType === 'download' ? 'export-btn' : 'status-btn';
    return `${baseClass} ${actionType}`;
  }, []);

  // 다운로드 액션은 제외 (별도 처리). requires 권한이 없는 액션은 숨긴다(비활성 아님)
  const filteredActions = useMemo(() => {
    return actions.filter(
      action => action.actionType !== 'download' && (!action.requires || hasPermission(action.requires)),
    );
  }, [actions, hasPermission]);

  if (!filteredActions || filteredActions.length === 0) {
    return null;
  }

  return (
    <div className="action-buttons">
      {filteredActions.map((action) => {
        const isEnabled = !action.enabledWhen || action.enabledWhen(selectedIds, items);
        const isActionLoading = actionLoading[action.id] || false;

        return (
          <button
            key={action.id}
            className={getActionClassName(action.actionType)}
            onClick={() => handleAction(action)}
            disabled={!isEnabled || isLoading || isActionLoading}
          >
            {isActionLoading ? (
              <>
                <FaSpinner className="spinner" /> 처리 중...
              </>
            ) : (
              <>
                {getActionIcon(action.actionType, action.icon)}
                {action.label}
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default React.memo(CampaignActionBar);

